/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpExportSide.tsx
 * FILE_ID: JOBHUB-QA-ITP-EXPORT-SIDE-001
 * PURPOSE: Ready-to-send panel, email fields and document options for ITP Export.
 * Reused from Quote Builder export page.
 */
import { useState } from 'react'
import { ChevronDown, SlidersHorizontal } from 'lucide-react'
import { cn } from '@quotech/ui'
import { ExportReadinessPanel } from '@quotech/quote-builder/components/export/ExportReadinessPanel'
import { QaItpEmailFields } from './QaItpEmailFields'
import type { useQaItpExport } from './useQaItpExport'

export function QaItpExportSide({
  model,
}: {
  model: ReturnType<typeof useQaItpExport>
}) {
  const [customiseOpen, setCustomiseOpen] = useState(false)
  const switchScope = (all: boolean) => {
    if (all === model.includeAll) return
    if (model.dirty && !window.confirm('Discard your changes to this ITP?')) return
    model.chooseScope(all)
  }

  return (
    <div className="space-y-3">
      <ExportReadinessPanel
        readiness={model.readiness}
        readyText="All checks passed. This ITP is ready to send."
      />
      <p className="text-xs text-muted-foreground">{model.checkCount} checks included</p>
      <QaItpEmailFields
        details={model.details}
        to={model.to}
        cc={model.cc}
        bcc={model.bcc}
        subject={model.subject}
        body={model.body}
        setTo={model.setTo}
        setCc={model.setCc}
        setBcc={model.setBcc}
        setSubject={model.setSubject}
        setBody={model.setBody}
        error={model.error}
        fromModel={model.fromModel}
        attachmentName={model.attachmentName}
        setAttachmentName={model.setAttachmentName}
        shareOnedrive={model.shareOnedrive}
        setShareOnedrive={model.setShareOnedrive}
        onedriveAllowed={model.onedriveAllowed}
        onedriveApproval={model.onedriveApproval}
        onCopyAdminLink={model.copyAdminLink}
        onSignInOnedrive={model.signInOnedrive}
        onUseAttachment={model.useOnedriveAttachment}
        coverUrl={model.coverPreviewUrl}
        coverBusy={model.coverBusy}
        onChooseCover={(file) => { void model.chooseCover(file) }}
        onRemoveCover={model.removeSavedCover}
        coverHeightMm={model.coverHeightMm}
        coverWidthMm={model.coverWidthMm}
        onCoverHeightMm={model.setCoverHeightMm}
        onCoverWidthMm={model.setCoverWidthMm}
        onCommitCoverSize={() => { void model.commitCoverSize() }}
        include={model.details.include}
        onIncludeChange={(key, next) => {
          const value = { ...model.details.include, [key]: next }
          void model.details.setInclude(key, next)
          model.patchIncludeInPlace(value)
        }}
      />
      <p className="text-[10px] leading-relaxed text-muted-foreground">
        The client does not need a Quotech login. Accept and Decline in the email open a public page, and the response is saved on this job and emailed back to you.
      </p>
      {model.signing.hasSignatures ? (
        <div className="space-y-1 text-xs">
          <p className="text-emerald-700">Signed copy ready. Print, Download PDF and Send use it.</p>
          <button type="button" className="text-xs font-medium text-primary" onClick={model.signing.removeSignatures}>
            {model.signing.count > 1 ? 'Remove signatures' : 'Remove signature'}
          </button>
          {model.signing.certificateLost ? (
            <p className="text-amber-700">
              Your edits removed the signing certificate page. Use Move or edit signature to add it again.
            </p>
          ) : null}
        </div>
      ) : null}
      <div className="mt-4 border-t border-slate-200 pt-3 dark:border-slate-700">
        <button
          type="button"
          onClick={() => setCustomiseOpen((open) => !open)}
          className="flex w-full items-center justify-between rounded px-1 py-1 text-left hover:bg-slate-50 dark:hover:bg-slate-800"
        >
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 dark:text-slate-300">
            <SlidersHorizontal className="h-3.5 w-3.5" /> Customise document
          </span>
          <ChevronDown className={cn('h-3.5 w-3.5 text-slate-400 transition-transform', customiseOpen && 'rotate-180')} />
        </button>
        {customiseOpen ? (
          <div className="mt-2 space-y-2">
            {model.hasSelection ? (
              <>
                <label className="flex items-center gap-2 text-xs">
                  <input type="radio" checked={!model.includeAll} onChange={() => switchScope(false)} />
                  Selected checks ({model.selectedCount})
                </label>
                <label className="flex items-center gap-2 text-xs">
                  <input type="radio" checked={model.includeAll} onChange={() => switchScope(true)} />
                  All checks in this checklist ({model.allCount})
                </label>
              </>
            ) : null}
            <button
              type="button"
              disabled={!model.dirty}
              onClick={model.discardEdits}
              className="rounded border border-border px-2 py-1 text-[11px] disabled:opacity-50"
            >
              Discard my edits
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
